import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogClose
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Alert, AlertDescription } from '../ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../ui/select';
import { AlertCircle, Clock, Loader2 } from 'lucide-react';
import { Progress } from '../ui/progress';
import { shopifyImportService } from '../../services/shopify/ShopifyImportService';
import { shopifyService } from '../../services/shopify/ShopifyService';
import { storeManagementService, IShopifyStore } from '../../services/StoreManagementService';

interface ImportDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  onImportComplete?: () => void;
}

const ImportDialog: React.FC<ImportDialogProps> = ({ 
  isOpen, 
  onOpenChange, 
  onImportComplete 
}) => {
  const [activeTab, setActiveTab] = useState('shopify');
  const [importMethod, setImportMethod] = useState<'bulk' | 'url' | 'search' | 'full'>('bulk');
  const [isLoading, setIsLoading] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [progress, setProgress] = useState(0);
  const [statusMessage, setStatusMessage] = useState('');
  const [initProgress, setInitProgress] = useState(0);
  const initProgressRef = useRef<NodeJS.Timeout>();
  const [stores, setStores] = useState<IShopifyStore[]>([]);

  // Shopify Import States
  const [shopifyStore, setShopifyStore] = useState('');
  const [bulkCount, setBulkCount] = useState(10);
  const [productUrls, setProductUrls] = useState('');
  const [searchQuery, setSearchQuery] = useState('');

  // Amazon Import States
  const [asinList, setAsinList] = useState('');

  // Load stores when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsLoading(true);
      storeManagementService.getStores()
        .then(stores => {
          setStores(stores);
          if (stores.length > 0) {
            setShopifyStore(stores[0].storeName);
          }
        })
        .catch(error => {
          console.error('Error loading stores:', error);
          setError('Failed to load stores. Please try again.');
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, [isOpen]);

  // Handle initialization progress animation
  useEffect(() => {
    if (isInitializing) {
      setInitProgress(0);
      const startTime = Date.now();
      const duration = 80000;
      const targetProgress = 90;

      const updateProgress = () => {
        const elapsed = Date.now() - startTime;
        const ratio = Math.min(elapsed / duration, 1);
        const easeInOutQuad = (t: number) => 
          t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2;
        const progress = Math.min(easeInOutQuad(ratio) * targetProgress, targetProgress);
        setInitProgress(progress);
        
        if (progress < targetProgress) {
          const updateDelay = ratio < 0.8 ? 50 : 100;
          initProgressRef.current = setTimeout(updateProgress, updateDelay);
        }
      };

      updateProgress();

      return () => {
        if (initProgressRef.current) {
          clearTimeout(initProgressRef.current);
        }
      };
    } else {
      setInitProgress(0);
    }
  }, [isInitializing]);

  // Prevent double initialization
  const initializationRef = useRef(false);

  // Single useEffect for state management
  useEffect(() => {
    if (!isOpen) {
      initializationRef.current = false;
      return;
    }

    const resetState = () => {
      setError('');
      setSuccess('');
      setProgress(0);
      setIsLoading(false);
      setIsInitializing(false);
      setBulkCount(10);
      setProductUrls('');
      setSearchQuery('');
      setAsinList('');
    };

    if (!initializationRef.current) {
      initializationRef.current = true;
      resetState();
    }
  }, [isOpen]);

  const getEstimatedTime = (): number => {
    if (importMethod === 'bulk' && bulkCount === 0) {
      return 600;
    }

    let count = 0;
    if (activeTab === 'shopify') {
      switch (importMethod) {
        case 'bulk':
          count = bulkCount;
          break;
        case 'url':
          count = productUrls.split('\n').filter(url => url.trim()).length;
          break;
        case 'search':
          count = 10;
          break;
      }
    } else {
      count = asinList.split('\n').filter(asin => asin.trim()).length;
    }
    return count * 30;
  };

  const handleImport = async () => {
    setIsInitializing(true);
    setError('');
    setStatusMessage('Connecting to database...');
    try {
      if (activeTab === 'shopify') {
        switch (importMethod) {
          case 'url':
            const urls = productUrls.split('\n').filter(url => url.trim());
            if (urls.length === 0) {
              throw new Error('Please enter at least one valid product URL');
            }
            break;
        }
      } else {
        const asins = asinList.split('\n')
          .map(asin => asin.trim())
          .filter(asin => asin.length === 10 && /^[A-Z0-9]{10}$/i.test(asin));

        if (asins.length === 0) {
          throw new Error('Please enter valid ASINs (10 characters each)');
        }
      }

      setIsLoading(true);
      setProgress(0);
      if (activeTab === 'shopify') {
        setStatusMessage('Starting import process...');
        const importOptions = {
          mode: importMethod as 'bulk' | 'url' | 'search',
          storeName: shopifyStore,
          ...(importMethod === 'bulk' && bulkCount > 0 && { maxProducts: bulkCount }),
          ...(importMethod === 'url' && { 
            urls: productUrls.split('\n')
              .map(url => url.trim())
              .filter(Boolean)
          }),
          ...(importMethod === 'search' && { query: searchQuery })
        };

        let totalProducts = 0;
        if (bulkCount === 0) {
          try {
            setStatusMessage('Getting total product count...');
            totalProducts = await shopifyService.getTotalProductCount(shopifyService.getStoreConfig(shopifyStore));
            importOptions.maxProducts = totalProducts;
            
            setStatusMessage(`Found ${totalProducts} products. Preparing import system...`);
            setTimeout(() => setStatusMessage(`Initializing database for ${totalProducts} products...`), 3000);
            setTimeout(() => setStatusMessage(`Setting up import process for ${totalProducts} products...`), 6000);
          } catch (error) {
            console.error('Error getting product count:', error);
            throw error;
          }
        }

        const phase1Duration = Math.ceil((totalProducts / 100) * 60000);
        const startTime = Date.now();

        shopifyImportService.importProducts(importOptions, (progress) => {
          if (bulkCount === 0 && totalProducts > 0) {
            const currentTime = Date.now();
            const elapsed = currentTime - startTime;
            const currentProduct = Math.floor((progress / 100) * totalProducts);
            
            if (progress < 50) {
              const phase1Progress = Math.min(45 * (elapsed / phase1Duration), 45);
              setProgress(phase1Progress);
              setStatusMessage(`Importing ${totalProducts} products - Initial import phase (${Math.floor(phase1Progress)}%)...`);
            } else if (progress < 90) {
              const phase2Progress = 45 + ((progress - 50) * 1.1);
              setProgress(Math.min(phase2Progress, 100));
              if (currentProduct > 0) {
                setStatusMessage(`Transforming product ${currentProduct} of ${totalProducts} (${Math.floor(phase2Progress)}% complete)...`);
              } else {
                setStatusMessage(`Transforming ${totalProducts} products (${Math.floor(phase2Progress)}% complete)...`);
              }
            } else {
              setProgress(Math.min(95 + (progress - 90) / 2, 100));
              setStatusMessage(`Finalizing import of ${totalProducts} products...`);
            }
          } else {
            setProgress(progress);
          }

          if (bulkCount === 0) {
            const currentProduct = Math.floor((progress / 100) * totalProducts);
            if (progress === 0) {
              setStatusMessage('Getting total product count...');
            } else if (progress < 25) {
              setStatusMessage(`Fetching details for ${totalProducts} products...`);
            } else if (progress < 50) {
              setStatusMessage(`Processing data for ${totalProducts} products...`);
            } else if (progress < 75) {
              setStatusMessage(`Transforming product ${currentProduct} of ${totalProducts}...`);
            } else if (progress < 100) {
              setStatusMessage(`Finalizing import of ${totalProducts} products...`);
            } else {
              setStatusMessage(`Successfully imported ${totalProducts} products!`);
            }
          } else {
            if (progress === 0) {
              setStatusMessage('Checking for existing products...');
            } else if (progress < 25) {
              setStatusMessage('Fetching product details...');
            } else if (progress < 50) {
              setStatusMessage('Processing product data...');
            } else if (progress < 75) {
              setStatusMessage('Importing products...');
            } else if (progress < 100) {
              setStatusMessage('Finalizing import...');
            } else {
              setStatusMessage('Import complete!');
            }
          }
        }).then(() => {
          setProgress(100);
          setSuccess('Import completed successfully');
          setIsLoading(false);

          if (onImportComplete) {
            onImportComplete();
          }

          requestAnimationFrame(() => {
            onOpenChange(false);
          });
        }).catch((err) => {
          let errorMessage = 'Import failed';
          let detailedError = '';
          
          if (err instanceof Error) {
            const apiError = (err as any).response?.data;
            if (apiError) {
              const details = apiError.errors || apiError.error || apiError.message;
              errorMessage = typeof details === 'string' ? details : JSON.stringify(details);
              
              if ((err as any).response?.status) {
                detailedError = `API Status: ${(err as any).response.status}\n`;
              }
              if (apiError.errors) {
                detailedError += `API Errors: ${JSON.stringify(apiError.errors, null, 2)}\n`;
              }
            } else {
              errorMessage = err.message;
            }
            
            console.error('Import error details:', {
              message: err.message,
              response: (err as any).response?.data,
              status: (err as any).response?.status
            });
          }
          
          setError(`${errorMessage}${detailedError ? `\n\nDebug Info:\n${detailedError}` : ''}`);
          setProgress(0);
          console.error('Import error:', err);
          setIsLoading(false);
        });
      } else {
        setError('Amazon import is not yet implemented');
      }
    } catch (err) {
      let errorMessage = 'Import failed';
      let detailedError = '';
      
      if (err instanceof Error) {
        const apiError = (err as any).response?.data;
        if (apiError) {
          const details = apiError.errors || apiError.error || apiError.message;
          errorMessage = typeof details === 'string' ? details : JSON.stringify(details);
          
          if ((err as any).response?.status) {
            detailedError = `API Status: ${(err as any).response.status}\n`;
          }
          if (apiError.errors) {
            detailedError += `API Errors: ${JSON.stringify(apiError.errors, null, 2)}\n`;
          }
        } else {
          errorMessage = err.message;
        }
        
        console.error('Import error details:', {
          message: err.message,
          response: (err as any).response?.data,
          status: (err as any).response?.status
        });
      }
      
      setError(`${errorMessage}${detailedError ? `\n\nDebug Info:\n${detailedError}` : ''}`);
      setProgress(0);
      console.error('Import error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onOpenChange(false);
  };

  const estimatedMinutes = Math.ceil(getEstimatedTime() / 60);

  return (
    <Dialog 
      open={isOpen} 
      onOpenChange={(open) => {
        if (!open && !isLoading) {
          document.body.style.pointerEvents = 'auto';
          onOpenChange(false);
        }
      }}
    >
      <DialogContent 
        className="sm:max-w-[600px]"
        onEscapeKeyDown={() => {
          if (!isLoading) {
            document.body.style.pointerEvents = 'auto';
            onOpenChange(false);
          }
        }}
        onInteractOutside={() => {
          if (!isLoading) {
            document.body.style.pointerEvents = 'auto';
            onOpenChange(false);
          }
        }}
      >
        <DialogHeader>
          <DialogTitle>Import Products</DialogTitle>
          <DialogDescription>
            Import products from Shopify or Amazon into the system.
          </DialogDescription>
        </DialogHeader>

        <Alert className="mb-4">
          <Clock className="h-4 w-4" />
          <AlertDescription>
            {bulkCount === 0 ? (
              <>Full store import may take about 1 minute per 50 products to complete.</>
            ) : (
              <>Import may take up to {estimatedMinutes} minute{estimatedMinutes !== 1 ? 's' : ''} to complete.</>
            )}
            {' '}Please do not close this window during import.
          </AlertDescription>
        </Alert>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="shopify">Shopify</TabsTrigger>
            <TabsTrigger value="amazon">Amazon</TabsTrigger>
          </TabsList>

          <TabsContent value="shopify" className="space-y-4">
            <div className="flex gap-2">
              <Button
                variant={importMethod === 'bulk' && bulkCount > 0 ? "default" : "outline"}
                onClick={() => {
                  setImportMethod('bulk');
                  setBulkCount(10);
                }}
                className={`flex-1 ${importMethod === 'bulk' && bulkCount > 0 ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                Bulk Import
              </Button>
              <Button
                variant={importMethod === 'bulk' && bulkCount === 0 ? "default" : "outline"}
                onClick={() => {
                  setImportMethod('bulk');
                  setBulkCount(0);
                }}
                className={`flex-1 ${importMethod === 'bulk' && bulkCount === 0 ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                Full Store Import
              </Button>
              <Button
                variant={importMethod === 'url' ? "default" : "outline"}
                onClick={() => setImportMethod('url')}
                className={`flex-1 ${importMethod === 'url' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                Product URLs
              </Button>
              <Button
                variant={importMethod === 'search' ? "default" : "outline"}
                onClick={() => setImportMethod('search')}
                className={`flex-1 ${importMethod === 'search' ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
              >
                Search
              </Button>
            </div>

            {importMethod === 'bulk' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Store</label>
                  <Select 
                    value={shopifyStore} 
                    onValueChange={setShopifyStore}
                    disabled={stores.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.length === 0 ? (
                        <SelectItem value="no-stores" disabled>
                          No stores available
                        </SelectItem>
                      ) : (
                        stores.map(store => (
                          <SelectItem key={store.id} value={store.storeName}>
                            {store.storeName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Number of Products</label>
                  <Input
                    type="number"
                    min={1}
                    value={bulkCount}
                    onChange={(e) => setBulkCount(parseInt(e.target.value))}
                    disabled={bulkCount === 0}
                  />
                  <p className="text-sm text-gray-500">
                    {bulkCount === 0
                      ? 'Full store import will import all products' 
                      : 'Enter the number of products to import'}
                  </p>
                </div>
              </div>
            )}

            {importMethod === 'url' && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Product URLs</label>
                <Textarea
                  value={productUrls}
                  onChange={(e) => setProductUrls(e.target.value)}
                  placeholder="Enter product URLs (one per line)"
                  className="min-h-[120px]"
                />
                <p className="text-sm text-gray-500">Maximum 20 URLs per import</p>
              </div>
            )}

            {importMethod === 'search' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <label className="text-sm font-medium">Select Store</label>
                  <Select 
                    value={shopifyStore} 
                    onValueChange={setShopifyStore}
                    disabled={stores.length === 0}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a store" />
                    </SelectTrigger>
                    <SelectContent>
                      {stores.length === 0 ? (
                        <SelectItem value="no-stores" disabled>
                          No stores available
                        </SelectItem>
                      ) : (
                        stores.map(store => (
                          <SelectItem key={store.id} value={store.storeName}>
                            {store.storeName}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium">Search Query</label>
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Enter search terms"
                  />
                </div>
              </div>
            )}
          </TabsContent>

          <TabsContent value="amazon" className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Amazon ASINs</label>
              <Textarea
                value={asinList}
                onChange={(e) => setAsinList(e.target.value)}
                placeholder="Enter ASINs (one per line)"
                className="min-h-[120px]"
              />
              <p className="text-sm text-gray-500">Maximum 20 ASINs per import</p>
            </div>
          </TabsContent>
        </Tabs>

        {(isLoading || isInitializing) && (
          <div className="my-4 space-y-2">
            <Progress 
              value={isInitializing ? initProgress : progress} 
              className="w-full transition-all duration-300 ease-in-out"
            />
            <p className="text-sm text-center text-gray-500">
              <span className="flex items-center justify-center">
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {isInitializing ? (
                  `${statusMessage || 'Preparing import...'}${initProgress < 90 ? ` (${Math.round(initProgress)}%)` : ''}`
                ) : (
                  progress === 100 ? 'Import complete!' : `Importing... ${Math.round(progress)}% complete`
                )}
              </span>
            </p>
          </div>
        )}

        {error && (
          <Alert variant="destructive" className="mt-4">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription className="whitespace-pre-wrap break-words font-mono text-sm">
              {error}
              {error.includes('429') && (
                <div className="mt-2 text-sm font-sans">
                  The request was rate limited. Please wait a moment and try again.
                </div>
              )}
              {error.includes('API Status') && (
                <div className="mt-2 text-xs opacity-80">
                  This error information has been logged to the console for debugging.
                </div>
              )}
            </AlertDescription>
          </Alert>
        )}

        {success && !isLoading && (
          <Alert className="mt-4 bg-green-50 text-green-800 border border-green-200">
            <AlertDescription>{success}</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end gap-4 mt-4">
          <DialogClose asChild>
            <Button 
              variant="outline" 
              onClick={handleClose}
              disabled={isLoading}
            >
              Cancel
            </Button>
          </DialogClose>
          <Button 
            onClick={handleImport} 
            disabled={isLoading || isInitializing || stores.length === 0}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {(isLoading || isInitializing) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isInitializing ? 'Preparing...' : isLoading ? 'Importing...' : 'Start Import'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ImportDialog;